// src/stores/questionStore.ts
import axios from 'axios';
import { uuid } from 'vue-uuid';
import { defineStore } from 'pinia';

import { createChatBody, fetchSuggestedQuestions } from '@/utils/chat';
import { getUsedSourceArray } from '@/utils/display';
import { Conversation, State } from '@/types/interfaces';

export const useQuestionStore = defineStore('questionStore', {
  state: (): State => ({
    session_id: uuid.v1(),
    conversations: [] as Conversation[],
    answerLoading: false,
    suggestionsLoading: false,
    error: null
  }),
  getters: {
    getConversations: (state) => state.conversations,
    getSessionId: (state) => state.session_id
  },
  actions: {
    async fetchAnswer(question: string, promptQuery?: string, view?: string) {
      let prompt = promptQuery || 'answers';
      const interactionId = uuid.v1();
      // add question to array first
      this.conversations.push({ question, interaction_id: interactionId });
      this.answerLoading = true;
      this.error = null;
      let response;
      const headers: { [key: string]: string } = {};

      try {
        const body = createChatBody(
          question,
          this.session_id,
          interactionId,
          this.conversations,
          prompt
        );
        if (process.env.NODE_ENV === 'development') {
          headers['x-functions-key'] = process.env.VUE_APP_X_FUNCTIONS_KEY;
        }
        response = await axios.post('/api/answers/getAIResponse', body, {
          headers
        });

        const sequencedSources = getUsedSourceArray(
          response.data.answer,
          response.data.sources
        );

        // update prompt if bot switch returned from API
        if (response.data?.bot_switch) {
          prompt = response.data.bot_switch;
        }

        // Update the last conversation with the answer and sources
        this.conversations[this.conversations.length - 1] = {
          question,
          interaction_id: interactionId,
          answer: response.data.answer,
          sources: sequencedSources,
          outcome: response.data.outcome,
          suggestedFollowOnQuestions: [],
          bot_switch: prompt
        };

        if (view === 'standard') {
          this.suggestionsLoading = true;
          const suggestedQuestions = await fetchSuggestedQuestions({
            question,
            answer: response.data.answer,
            session_id: this.session_id,
            interaction_id: interactionId,
            headers
          });

          this.conversations[
            this.conversations.length - 1
          ].suggestedFollowOnQuestions = suggestedQuestions;
        }
      } catch (error) {
        this.error = 'Failed to fetch answer. Please try again.';
        return;
      } finally {
        this.answerLoading = false;
        this.suggestionsLoading = false;
      }
    }
  }
});
